/**
 * The HTTP client. One `request()` funnel, so there is exactly one error path
 * (`ApiProblem`) and exactly one place that knows the base URL.
 *
 * Presigned URLs are fetched per image and never cached here: the service mints them with a
 * short TTL, and a URL kept in client state outlives its signature and starts 403-ing at the
 * least convenient moment. They are also never logged — `console.log(url)` would put a signed
 * request in the browser's console for anyone looking over the annotator's shoulder.
 *
 * Authentication
 * --------------
 * Every request carries `Authorization: Bearer <token>` from the session store, and a 401 ends
 * the session in one place (`handleUnauthorized`) rather than in each caller. The token is
 * never logged and — like a presigned URL — never travels in a query string, which would put
 * it in a browser history entry and in every access log along the way.
 *
 * `annotator_id` is **not** a parameter of anything here any more. It used to be a field on
 * every write, which made a box's author whatever the client typed; the service now derives it
 * from the token, and there is no way to express it from this side.
 */

import { auth, handleUnauthorized } from '../stores/auth.svelte';
import { ApiProblem, asProblem, problemFromResponse } from './problem';
import type {
  AcceptInviteRequest,
  AnnotatorOut,
  BackupRunOut,
  BackupSummary,
  BulkUpsertRequest,
  BulkUpsertResponse,
  ConnectionTestResult,
  DeleteResponseLike,
  EffectiveSettings,
  GuidelinesResponse,
  Health,
  ImageAnnotations,
  ImageDetail,
  ImageStatusRequest,
  InscriptionDetail,
  InscriptionPage,
  InscriptionPatch,
  InscriptionQuery,
  InscriptionResetResult,
  InscriptionResetSummary,
  InviteCreated,
  InviteOut,
  InvitePreview,
  LockClearRequest,
  LockClearResponse,
  LockInventory,
  PresignedImageUrl,
  SettingsStatus,
  SettingsUpdate,
  StatsResponse,
  TaskListResponse,
  TaskQueuePage,
  TokenResponse,
  UserList,
  UserOut,
  UserPatch,
  VocabResponse,
} from './types';

/** Same-origin by default: Vite proxies `/api`, and FastAPI can mount `dist/` at `/`. */
const BASE = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '';

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** A form-encoded body, for the OAuth2 password flow `POST /api/auth/login` expects. */
  form?: Record<string, string>;
  /**
   * Skip the `Authorization` header and the 401 handling.
   *
   * For the three public routes only — sign-in and the invite pair. Sending a stale token to
   * the login endpoint would be harmless, but letting its 401 run `handleUnauthorized` would
   * mean a mistyped password also wiped a live session in another tab.
   */
  anonymous?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const suffix = search.toString();
  return `${BASE}${path}${suffix ? `?${suffix}` : ''}`;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = options.anonymous ? {} : auth.authHeader();
  if (options.form !== undefined) {
    headers['Content-Type'] = 'application/x-www-form-urlencoded';
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const init: RequestInit = { method: options.method ?? 'GET', headers };
  if (options.form !== undefined) init.body = new URLSearchParams(options.form).toString();
  else if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.signal) init.signal = options.signal;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), init);
  } catch (error) {
    throw asProblem(error);
  }
  if (!response.ok) {
    const problem = await problemFromResponse(response);
    // One place ends the session, so no caller has to remember to. It matters most for the
    // autosave loop, which must not treat a dead token as a retryable failure and spin.
    if (!options.anonymous) handleUnauthorized(problem);
    throw problem;
  }
  if (response.status === 204) return undefined as T;
  try {
    return (await response.json()) as T;
  } catch (error) {
    throw asProblem(error);
  }
}

export const api = {
  health: () => request<Health>('/api/health', { anonymous: true }),

  // ── Authentication ────────────────────────────────────────────────────────

  /** OAuth2 password flow: a **form** body, with the email as `username`. */
  login: (email: string, password: string) =>
    request<TokenResponse>('/api/auth/login', {
      method: 'POST',
      form: { username: email, password },
      anonymous: true,
    }),

  /**
   * Re-read the signed-in account from the server.
   *
   * Called on load rather than trusting the cached user: authorization is the row's `role`,
   * not a claim in the token, so a demotion or a deactivation takes effect on the next page
   * load instead of the next sign-in.
   */
  me: () => request<UserOut>('/api/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<UserOut>('/api/auth/change-password', {
      method: 'POST',
      body: { current_password: currentPassword, new_password: newPassword },
    }),

  /** Public: what an invite link is for. Always 200 — an unknown token is `valid: false`. */
  previewInvite: (token: string) =>
    request<InvitePreview>(`/api/auth/invites/${encodeURIComponent(token)}`, {
      anonymous: true,
    }),

  /** Public: redeem an invite. The role comes from the invite, never from this call. */
  acceptInvite: (payload: AcceptInviteRequest) =>
    request<TokenResponse>('/api/auth/accept-invite', {
      method: 'POST',
      body: payload,
      anonymous: true,
    }),

  // ── Administration (admin role) ───────────────────────────────────────────

  /** The response is the only time the invite token exists outside a SHA-256. */
  createInvite: (email: string, role: string) =>
    request<InviteCreated>('/api/auth/invites', { method: 'POST', body: { email, role } }),

  invites: () => request<InviteOut[]>('/api/auth/invites'),

  revokeInvite: (inviteId: number) =>
    request<InviteOut>(`/api/auth/invites/${inviteId}`, { method: 'DELETE' }),

  users: () => request<UserList>('/api/admin/users'),

  patchUser: (userId: number, payload: UserPatch) =>
    request<UserOut>(`/api/admin/users/${userId}`, { method: 'PATCH', body: payload }),

  // ── Corpus and annotation ─────────────────────────────────────────────────

  vocab: () => request<VocabResponse>('/api/vocab'),
  guidelines: () => request<GuidelinesResponse>('/api/docs/guidelines'),

  // ── Tasks: what there is to do, and what is left of it ────────────────────

  /**
   * Every task, with its own numbers. Nothing is filtered by who is asking: tasks are not
   * gated, and the card states the background each one calls for so the reader can judge.
   */
  tasks: () => request<TaskListResponse>('/api/tasks'),

  /**
   * One task's remaining work, in priority order.
   *
   * There is no filter argument and there should not be. A work queue answers "what next",
   * which is a question about ordering; "show me records like this" is the browser's question
   * and `inscriptions()` above is where it goes.
   */
  taskQueue: (taskId: string, query: { limit?: number; offset?: number } = {}) =>
    request<TaskQueuePage>(`/api/tasks/${encodeURIComponent(taskId)}/queue`, { query }),

  // ── Corpus browsing and annotation ────────────────────────────────────────

  inscriptions: (query: InscriptionQuery = {}, signal?: AbortSignal) =>
    request<InscriptionPage>('/api/inscriptions', {
      query: query as Record<string, string | number | boolean | undefined>,
      ...(signal ? { signal } : {}),
    }),

  /**
   * The real accessor. `GET /api/inscriptions/{siglum}` exists for a human typing a citation
   * into a URL bar, but a siglum is a storage prefix and not a key: four OCIANA records share
   * one, and that route answers 409 `siglum_ambiguous` rather than silently picking a stone.
   * Every work-queue row carries `id`, so the SPA never needs the siglum route.
   */
  inscriptionById: (id: number) => request<InscriptionDetail>(`/api/inscriptions/by-id/${id}`),

  inscriptionBySiglum: (siglum: string) =>
    request<InscriptionDetail>(`/api/inscriptions/${encodeURIComponent(siglum)}`),

  imageUrl: (imageId: number) => request<PresignedImageUrl>(`/api/images/${imageId}/url`),

  imageAnnotations: (imageId: number) =>
    request<ImageAnnotations>(`/api/images/${imageId}/annotations`),

  /** The autosave write path: the whole box set for one image, diffed server-side. */
  bulkUpsert: (imageId: number, payload: BulkUpsertRequest) =>
    request<BulkUpsertResponse>(`/api/images/${imageId}/annotations`, {
      method: 'POST',
      body: payload,
    }),

  setImageStatus: (imageId: number, payload: ImageStatusRequest) =>
    request<ImageDetail>(`/api/images/${imageId}/status`, { method: 'POST', body: payload }),

  /**
   * Reading direction and layout, without touching a box.
   *
   * Addressed by **inscription**: the direction is a fact about the writing, so one call
   * settles every photograph of the stone. This was `PATCH /api/images/{id}` until
   * 2026-08-09, when answering it per photograph meant the siblings listed in the box editor
   * could each say something different.
   *
   * Not `bulkUpsert`, which can no longer express a direction at all — its box list is
   * authoritative, so sending one with an empty `annotations` array deleted every box on the
   * image.
   */
  patchInscription: (inscriptionId: number, payload: InscriptionPatch) =>
    request<InscriptionDetail>(`/api/inscriptions/by-id/${inscriptionId}`, {
      method: 'PATCH',
      body: payload,
    }),

  /**
   * What a reset of this record would clear, as of now.
   *
   * Asked of the service rather than added up locally, on purpose: the confirmation this
   * feeds has to state figures that are true at the moment it is shown, including boxes
   * somebody else drew after this page loaded.
   */
  resetPreview: (inscriptionId: number) =>
    request<InscriptionResetSummary>(`/api/inscriptions/by-id/${inscriptionId}/reset`),

  /**
   * Return a record to the state the corpus delivered it in: every box and not-present mark
   * on every one of its photographs, their statuses, and the record's reading direction and
   * layout. There is no undo, and `confirm` never defaults to true.
   */
  resetInscription: (inscriptionId: number, payload: { confirm: true; reason?: string }) =>
    request<InscriptionResetResult>(`/api/inscriptions/by-id/${inscriptionId}/reset`, {
      method: 'POST',
      body: payload,
    }),

  /**
   * Delete one box. It carries provenance, as every write does — the token's now, rather than
   * an `annotator_id` query parameter, which also keeps an annotator id out of every access
   * log line between here and the service.
   */
  deleteAnnotation: (annotationId: number) =>
    request<DeleteResponseLike>(`/api/annotations/${annotationId}`, { method: 'DELETE' }),

  /**
   * Set an annotator's display name. **Admin only** — the name is what a lock conflict shows
   * and what the agreement report groups on, so it is not something any caller may rewrite
   * for anyone. An ordinary account's name follows its user record, which
   * `PATCH /api/admin/users/{id}` keeps in step.
   */
  registerAnnotator: (annotatorId: string, name: string) =>
    request<AnnotatorOut>(`/api/annotators/${encodeURIComponent(annotatorId)}`, {
      method: 'PUT',
      body: { name },
    }),

  /** Which locks are held. `held_by_caller` follows the bearer token, not a parameter. */
  locks: () => request<LockInventory>('/api/locks'),

  /**
   * Release locks. `scope: 'all'` is refused by the service unless the body also carries
   * `confirm_release_live: true` — do not default that field on here, and do not call this
   * from anything that is not behind a confirmation the operator read.
   */
  clearLocks: (payload: LockClearRequest) =>
    request<LockClearResponse>('/api/locks/clear', { method: 'POST', body: payload }),

  settings: () => request<EffectiveSettings>('/api/settings'),

  saveSettings: (payload: SettingsUpdate) =>
    request<EffectiveSettings>('/api/settings', { method: 'PUT', body: payload }),

  testConnection: () => request<ConnectionTestResult>('/api/settings/test', { method: 'POST' }),

  /**
   * Recent backup runs and the age of the newest complete one. Admin only.
   *
   * An incomplete run — members with no manifest — is a failed upload, so the newest *good*
   * copy is older than a naive listing would suggest. The service reports both.
   */
  backups: () => request<BackupSummary>('/api/admin/backups'),

  /** Take a backup now. Admin only; the daily one is a Fly scheduled machine. */
  runBackup: (reason?: string) =>
    request<BackupRunOut>('/api/admin/backups', {
      method: 'POST',
      body: reason ? { reason } : {},
    }),

  settingsStatus: () => request<SettingsStatus>('/api/settings/status'),

  /**
   * The home dashboard's numbers. Fetched **once** with a manual refresh — never polled: these
   * move when somebody annotates, not on a timer, and a full-corpus aggregate re-run every few
   * seconds spends the service's throughput on a page nobody is looking at.
   */
  stats: () => request<StatsResponse>('/api/stats'),
};

export { ApiProblem };
